import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Trash2,
  Send,
  Check,
  X as XIcon,
  ChevronLeft,
  ChevronRight,
  Loader2,
  BookOpen,
  Pencil,
  Image,
  FileAudio,
  Video,
  Bot,
} from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { useI18n } from "@/lib/i18n";
import {
  ModulePanel,
  EditorialStatusBadge,
} from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { RefusArticleDialog, DerogationDroitReponseDialog } from "@/components/admin/DecisionEditorialeDialogs";
import {
  useArticles,
  useMesArticles,
  useDeleteArticle,
  usePublishArticle,
  useValiderArticle,
  useRefuserArticle,
  estBlocageDroitDeReponse,
} from "@/hooks/useArticles";
import { useCategories } from "@/hooks/useCategories";
import { useTypesArticle } from "@/hooks/useTypesArticle";
import { usePays } from "@/hooks/usePays";
import { useUtilisateurs } from "@/hooks/useUtilisateurs";
import { hasAnyRole, useBloquerPartenaire } from "@/pages/admin/RequireAdmin";
import { ArticleDto } from "@/types/article.types";
import {
  TYPE_CONTENU_FROM_API,
  SIGNATURE_LABEL,
  STATUT_MAP,
  STATUTS_FILTRES,
  LANGUES_CONFIG,
} from "@/lib/enums";

// ─── Page principale Articles ─────────────────────────────────────────────────
function Content() {
  useBloquerPartenaire();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [statutFiltre, setStatutFiltre] = useState("");
  const [paysFiltre, setPaysFiltre] = useState("");
  const [categorieFiltre, setCategorieFiltre] = useState("");
  const [typeFiltre, setTypeFiltre] = useState("");
  const [auteurFiltre, setAuteurFiltre] = useState("");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ArticleDto | null>(null);
  const [refuseTarget, setRefuseTarget] = useState<ArticleDto | null>(null);
  // Validation bloquée par un droit de réponse en attente : proposée en dérogation.
  const [derogation, setDerogation] = useState<{ article: ArticleDto; via: "valider" | "publier" } | null>(null);

  const { data: categories } = useCategories();
  const { data: typesArticle } = useTypesArticle();
  const { data: pays } = usePays();
  const { data: utilisateurs } = useUtilisateurs();
  const categoryList = Array.isArray(categories) ? categories : [];
  const typeArticleList = Array.isArray(typesArticle) ? typesArticle : [];
  const paysList = Array.isArray(pays) ? pays : [];
  // Auteurs pouvant être filtrés depuis cette liste : uniquement les comptes
  // qui écrivent réellement du contenu (pas les admins/éditeurs/vérificateurs).
  const auteurList = (Array.isArray(utilisateurs) ? utilisateurs : []).filter((u: any) =>
    (u.roles || []).some((r: string) => r === "REDACTEUR" || r === "PARTENAIRE")
  );

  // Un compte qui n'a que le rôle REDACTEUR ne doit voir que ses propres
  // articles, pas ceux de toute la rédaction.
  const estRedacteurSeul = hasAnyRole(["REDACTEUR"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);

  const { data, isLoading: loadingTous, refetch: refetchTous } = useArticles(
    page,
    20,
    statutFiltre || undefined,
    {
      paysIds: paysFiltre ? [Number(paysFiltre)] : undefined,
      categorieId: categorieFiltre ? Number(categorieFiltre) : undefined,
      type: typeFiltre || undefined,
      auteurId: auteurFiltre || undefined,
    },
    !estRedacteurSeul,
  );
  const { data: mesArticlesData, isLoading: loadingMiens, refetch: refetchMiens } = useMesArticles(estRedacteurSeul);

  const isLoading = estRedacteurSeul ? loadingMiens : loadingTous;
  const refetch = estRedacteurSeul ? refetchMiens : refetchTous;

  const deleteArticle = useDeleteArticle();
  const publishArticle = usePublishArticle();
  const validerArticle = useValiderArticle();
  const refuserArticle = useRefuserArticle();

  const articles: ArticleDto[] = estRedacteurSeul
    ? (Array.isArray(mesArticlesData) ? mesArticlesData : [])
    : (data?.content || data || []);
  const totalPages = estRedacteurSeul ? 1 : (data?.totalPages || 1);

  const filtered = articles.filter((a) => {
    if (estRedacteurSeul && statutFiltre && a.statut !== statutFiltre) return false;
    if (!search) return true;
    const titre = a.traductions?.[0]?.titre?.toLowerCase() || "";
    return (
      titre.includes(search.toLowerCase()) ||
      a.slug.includes(search.toLowerCase())
    );
  });

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteArticle.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        refetch();
      },
      onError: () => toast.error(t("admin.common.deleteError")),
    });
  };

  const confirmRefuse = (commentaire: string, definitif: boolean) => {
    if (!refuseTarget || !commentaire) return;
    refuserArticle.mutate(
      { id: refuseTarget.id, commentaire, definitif },
      {
        onSuccess: () => {
          toast.success(definitif ? "Article rejeté et archivé" : "Révision demandée, le contributeur a été notifié");
          setRefuseTarget(null);
          refetch();
        },
        onError: () => toast.error(t("admin.common.saveError")),
      }
    );
  };

  const decider = (a: ArticleDto, via: "valider" | "publier", forcer = false) => {
    const options = {
      onSuccess: () => {
        toast.success(via === "valider" ? "Article validé et publié" : "Article publié");
        setDerogation(null);
        refetch();
      },
      onError: (e: any) => {
        if (estBlocageDroitDeReponse(e)) setDerogation({ article: a, via });
        else toast.error(e?.response?.data?.message || t("admin.common.saveError"));
      },
    };
    if (via === "valider") validerArticle.mutate({ id: a.id, forcer }, options);
    else publishArticle.mutate({ id: a.id, forcer }, options);
  };

  // Articles en attente d'une décision de la rédaction : brouillon d'un
  // rédacteur, ou article soumis (relecture / validation juridique).
  const enAttenteDeDecision = (statut: string) =>
    statut === "BROUILLON" || statut === "RELECTURE" || statut === "JURIDIQUE";

  return (
    <>
      <AdminTopbar
        title={t("search.articles")}
        subtitle={t("admin.content.subtitle")}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {STATUTS_FILTRES.map((s) => (
              <button
                key={s.value}
                onClick={() => {
                  setStatutFiltre(s.value);
                  setPage(0);
                }}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  statutFiltre === s.value
                    ? "bg-foreground text-background"
                    : "border border-border text-muted-foreground hover:text-foreground hover:bg-surface"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!estRedacteurSeul && (
              <>
                <select
                  value={paysFiltre}
                  onChange={(e) => { setPaysFiltre(e.target.value); setPage(0); }}
                  className="rounded-md border border-border bg-surface px-2 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                >
                  <option value="">Tous les pays</option>
                  {paysList.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
                <select
                  value={categorieFiltre}
                  onChange={(e) => { setCategorieFiltre(e.target.value); setPage(0); }}
                  className="rounded-md border border-border bg-surface px-2 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                >
                  <option value="">Toutes les catégories</option>
                  {categoryList.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.libelleFr}</option>
                  ))}
                </select>
                <select
                  value={auteurFiltre}
                  onChange={(e) => { setAuteurFiltre(e.target.value); setPage(0); }}
                  className="rounded-md border border-border bg-surface px-2 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                >
                  <option value="">Tous les auteurs</option>
                  {auteurList.map((u: any) => (
                    <option key={u.id} value={u.id}>{u.nom || u.nomAffichage || u.email}{u.code ? ` (${u.code})` : ""}</option>
                  ))}
                </select>
                {/* <select
                  value={typeFiltre}
                  onChange={(e) => { setTypeFiltre(e.target.value); setPage(0); }}
                  className="rounded-md border border-border bg-surface px-2 py-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                >
                  <option value="">Tous les types</option>
                  {typeArticleList.map((ta: any) => (
                    <option key={ta.id} value={ta.code}>{ta.libelle}</option>
                  ))}
                </select> */}
              </>
            )}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("admin.common.search")}
                className="rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground w-48"
              />
            </div>
            <button
              onClick={() => navigate("/redaction/articles/nouveau")}
              className="flex items-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-semibold text-background hover:bg-foreground/90 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> {t("admin.common.new")}
            </button>
          </div>
        </div>

        <ModulePanel title={`${t("search.articles")} (${filtered.length})`}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t("admin.common.loading")}</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <BookOpen className="h-8 w-8 opacity-30" />
              <p className="text-sm">{t("admin.content.noArticle")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">{t("admin.common.col.title")}</th>
                    <th className="px-3 py-2">Auteur</th>
                    <th className="px-3 py-2">{t("admin.common.col.type")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.status")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.languages")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.date")}</th>
                    <th className="px-3 py-2 text-right">{t("admin.common.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b border-rule last:border-0 hover:bg-surface/60 group cursor-pointer"
                      onClick={() => navigate(`/redaction/articles/${a.id}`)}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-foreground line-clamp-1 max-w-xs">
                          {a.deposeParNewsroom && (
                            <span
                              title="Déposé par la newsroom IA"
                              className="mr-1.5 inline-flex items-center gap-0.5 rounded bg-surface px-1 py-0.5 align-middle text-[9px] font-bold uppercase text-muted-foreground"
                            >
                              <Bot className="h-2.5 w-2.5" /> IA
                            </span>
                          )}
                          {a.traductions?.[0]?.titre || (
                            <span className="italic text-muted-foreground">
                              {a.slug}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {a.signature && (
                            <span className="text-[10px] text-muted-foreground">
                              {SIGNATURE_LABEL[a.signature] ?? a.signature}
                            </span>
                          )}
                          {/* Indicateurs de médias */}
                          {a.medias?.some((m) => m.type === "image") && (
                            <Image className="h-2.5 w-2.5 text-muted-foreground/60" />
                          )}
                          {a.medias?.some((m) => m.type === "video") && (
                            <Video className="h-2.5 w-2.5 text-muted-foreground/60" />
                          )}
                          {a.medias?.some((m) => m.type === "audio") && (
                            <FileAudio className="h-2.5 w-2.5 text-muted-foreground/60" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">
                        {a.auteurNom || "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-mono text-xs text-muted-foreground">
                          {TYPE_CONTENU_FROM_API[a.type] ??
                            a.type.toLowerCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <EditorialStatusBadge
                          status={
                            estRedacteurSeul && a.statut === "BROUILLON"
                              ? "En attente"
                              : STATUT_MAP[a.statut] || a.statut
                          }
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex gap-1">
                          {LANGUES_CONFIG.map((l) => (
                            <span
                              key={l.code}
                              className={`text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${
                                (a.languesDisponibles ?? []).some(
                                  (ld) => ld.toLowerCase() === l.code,
                                )
                                  ? "bg-verified/15 text-verified"
                                  : "bg-surface text-muted-foreground/40"
                              }`}
                            >
                              {l.code}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {a.modifieLe || a.publieLe
                          ? new Date(
                              a.modifieLe || a.publieLe!,
                            ).toLocaleDateString("fr-FR")
                          : "—"}
                      </td>
                      <td
                        className="px-3 py-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => navigate(`/redaction/articles/${a.id}`)}
                            title="Modifier / Traductions"
                            className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {!estRedacteurSeul && (enAttenteDeDecision(a.statut) ? (
                            <>
                              <button
                                onClick={() => decider(a, "valider")}
                                title="Valider (publier)"
                                className="rounded p-1 text-muted-foreground hover:text-verified hover:bg-verified/10 transition-colors"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => setRefuseTarget(a)}
                                title="Refuser"
                                className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                              >
                                <XIcon className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : a.statut !== "PUBLIE" && (
                            <button
                              onClick={() => decider(a, "publier")}
                              title="Publier"
                              className="rounded p-1 text-muted-foreground hover:text-verified hover:bg-verified/10 transition-colors"
                            >
                              <Send className="h-3.5 w-3.5" />
                            </button>
                          ))}
                          {!estRedacteurSeul && (
                            <button
                              title="Supprimer"
                              onClick={() => setDeleteTarget(a)}
                              className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
              <span className="text-xs text-muted-foreground">
                {t("admin.common.page", { page: page + 1, total: totalPages })}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </ModulePanel>
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.article.delete.title")}
        description={
          deleteTarget
            ? `« ${deleteTarget.traductions?.[0]?.titre || deleteTarget.slug} » ${t("admin.article.delete.desc")}`
            : undefined
        }
        onConfirm={handleDelete}
        loading={deleteArticle.isPending}
      />

      <RefusArticleDialog
        titreArticle={refuseTarget?.traductions?.[0]?.titre || refuseTarget?.slug}
        open={!!refuseTarget}
        onOpenChange={(open) => !open && setRefuseTarget(null)}
        onConfirm={confirmRefuse}
        loading={refuserArticle.isPending}
      />

      <DerogationDroitReponseDialog
        open={!!derogation}
        onOpenChange={(open) => !open && setDerogation(null)}
        onConfirm={() => derogation && decider(derogation.article, derogation.via, true)}
        loading={validerArticle.isPending || publishArticle.isPending}
      />
    </>
  );
}

export default Content;
